import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import ClientFormDialog from '../components/common/ClientFormDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import JobsTable from '../components/jobs/JobsTable';
import JobFormDrawer from '../components/jobs/JobFormDrawer';
import ActivityList from '../components/activity/ActivityList';
import QuotesTable from '../components/quotes/QuotesTable';
import QuoteFormDialog from '../components/quotes/QuoteFormDialog';
import { EmptyState, InfoRow, SectionCard } from '../components/common/ui';
import ClientService from '../services/ClientService';
import JobService from '../services/JobService';
import QuoteService from '../services/QuoteService';
import { errorMessage } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageRefresh } from '../components/layout/pullToRefresh';

export default function ClientDetailPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { isAdmin } = useAuth();
    const [client, setClient] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [jobs, setJobs] = useState({ items: [], total: 0 });
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [jobOpen, setJobOpen] = useState(false);
    const [version, setVersion] = useState(0);   // bumps after an edit so the activity list reloads
    const [quotes, setQuotes] = useState({ items: [], total: 0 });
    const [quoteOpen, setQuoteOpen] = useState(false);

    const loadClient = useCallback(() => ClientService.get(id).then(setClient).catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else showToast(t('common.loadError'), 'error');
    }), [id, showToast, t]);
    useEffect(() => { setClient(null); setNotFound(false); loadClient(); }, [loadClient]);

    const loadJobs = useCallback(() => {
        return JobService.list({ clientId: id, page, size, sort: 'created' }).then(setJobs).catch(() => {});
    }, [id, page, size]);
    useEffect(() => { loadJobs(); }, [loadJobs]);

    const clientDefaults = useMemo(() => ({ client }), [client]);

    const loadQuotes = useCallback(() => (isAdmin ? QuoteService.list({ clientId: id, size: 100 }).then(setQuotes).catch(() => {}) : Promise.resolve()), [id, isAdmin]);
    useEffect(() => { loadQuotes(); }, [loadQuotes]);
    // Pull-to-refresh: the client, their jobs and quotes, and the activity list (version).
    usePageRefresh(() => { setVersion(v => v + 1); return Promise.all([loadClient(), loadJobs(), loadQuotes()]); });

    const remove = async () => {
        try {
            await ClientService.remove(client.id);
            showToast(t('clients.toast.deleted'), 'success');
            navigate('/clients', { replace: true });
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            setDeleteOpen(false);
        }
    };

    if (notFound) {
        return <Layout><Box sx={{ textAlign: 'center', mt: 10 }}><Typography variant="h6" fontWeight={700}>{t('clients.notFound')}</Typography>
            <Button sx={{ mt: 2 }} onClick={() => navigate('/clients')}>{t('clients.backToClients')}</Button></Box></Layout>;
    }
    if (!client) return <Layout><Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box></Layout>;

    return (
        <Layout>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/clients')} sx={{ color: '#64748B', mb: 1, ml: -1 }}>{t('clients.title')}</Button>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8 }}>{t(`clients.type.${client.type}`)}</Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', letterSpacing: -0.3 }}>{client.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditOpen(true)}>{t('common.edit')}</Button>
                    <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteOpen(true)}>{t('common.delete')}</Button>
                    {isAdmin && <Button variant="outlined" startIcon={<RequestQuoteOutlinedIcon />} onClick={() => setQuoteOpen(true)}>{t('quotes.new')}</Button>}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setJobOpen(true)}>{t('jobs.new')}</Button>
                </Box>
            </Box>

            <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, lg: 4 }}>
                    <SectionCard title={t('clients.details')}>
                        <Box sx={{ px: 2.5, py: 1 }}>
                            <InfoRow label={client.type === 'COMPANY' ? t('clients.fields.businessNumber') : t('clients.fields.personalNumber')}>{client.idNumber}</InfoRow>
                            <InfoRow label={t('clients.fields.phone')}>{client.phone && <Link href={`tel:${client.phone}`} underline="hover" sx={{ color: '#427898' }}>{client.phone}</Link>}</InfoRow>
                            <InfoRow label={t('clients.fields.email')}>{client.email && <Link href={`mailto:${client.email}`} underline="hover" sx={{ color: '#427898' }}>{client.email}</Link>}</InfoRow>
                            <InfoRow label={t('clients.fields.address')}>{[client.address, client.city].filter(Boolean).join(', ') || null}</InfoRow>
                            <InfoRow label={t('clients.fields.notes')}>{client.notes && <Box sx={{ whiteSpace: 'pre-wrap' }}>{client.notes}</Box>}</InfoRow>
                        </Box>
                    </SectionCard>
                    {/* The client's own changes and those of its jobs. */}
                    <SectionCard title={t('activity.title')} sx={{ mt: 2.5 }}>
                        <ActivityList clientId={client.id} reloadKey={`${version}-${jobs.total}`} showSubject pageSize={10} />
                    </SectionCard>
                </Grid>
                <Grid size={{ xs: 12, lg: 8 }}>
                    {isAdmin && quotes.items.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a2332', mb: 1.5 }}>{t('quotes.clientTitle', { count: quotes.total })}</Typography>
                            <QuotesTable quotes={quotes.items} hideClient />
                        </Box>
                    )}
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a2332', mb: 1.5 }}>{t('clients.jobsTitle', { count: jobs.total })}</Typography>
                    {jobs.items.length ? (
                        <JobsTable jobs={jobs.items} total={jobs.total} page={page} size={size} hideClient
                                   onPage={setPage} onSize={(s) => { setSize(s); setPage(0); }} />
                    ) : (
                        <EmptyState icon={<WorkOutlineIcon />} title={t('clients.noJobs')}
                                    action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setJobOpen(true)}>{t('jobs.new')}</Button>} />
                    )}
                </Grid>
            </Grid>

            <ClientFormDialog open={editOpen} client={client} onClose={() => setEditOpen(false)} onSaved={(c) => { setClient(c); setEditOpen(false); setVersion(v => v + 1); }} />
            <ConfirmDialog open={deleteOpen} title={t('clients.deleteTitle')}
                           text={jobs.total ? t('clients.deleteBlocked', { count: jobs.total })
                               : quotes.total ? t('quotes.clientDeleteBlocked', { count: quotes.total })
                               : t('clients.deleteText', { name: client.name })}
                           onConfirm={remove} onClose={() => setDeleteOpen(false)} />
            <QuoteFormDialog open={quoteOpen} defaults={clientDefaults} onClose={() => setQuoteOpen(false)}
                             onSaved={(q) => { setQuoteOpen(false); navigate(`/quotes/${q.id}`); }} />
            <JobFormDrawer open={jobOpen} defaults={clientDefaults} onClose={() => setJobOpen(false)}
                           onSaved={(j) => { setJobOpen(false); navigate(`/jobs/${j.id}`); }} />
        </Layout>
    );
}
